import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ChatSlice } from '../../types/interface';

const initialState: ChatSlice = {
  allChats: [],
  status: 'idle',
  error: null,
};

export const fetchMessages = createAsyncThunk(
  'chat/fetchMessages',
  async (params: { token: string; userId: string; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/b2c/chat`, {
        headers: {
          Authorization: `Bearer ${params.token}`,
        },
        params: {
          limit: params.limit,
          userId: params.userId,
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch messages');
    }
  }
);

export const postMessage = createAsyncThunk(
  'chat/postMessage',
  async (
    params: { content: string; toUserId: string; fromUserId: string; token: string; indId: string },
    { rejectWithValue }
  ) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/b2c/chat`,
        {
          content: params.content,
          toUserId: params.toUserId,
          fromUserId: params.fromUserId,
          indId: params.indId,
        },
        {
          headers: {
            Authorization: `Bearer ${params.token}`,
          },
        }
      );
      return { content: params.content, toUserId: params.toUserId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to send message');
    }
  }
);

// Define the chat slice
const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMessages.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.allChats = action.payload;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });

    builder
      .addCase(postMessage.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(postMessage.fulfilled, (state, action) => {
        state.status = 'succeeded';

        state.allChats.push({
          id: Date.now(), // temporary; gets changed on backend
          from_user_id: 'self', // temporary; gets changed on backend
          from_user_type: 'user',
          to_user_id: action.payload.toUserId,
          to_user_type: 'provider',
          content: action.payload.content,
          created_at: new Date().toISOString(),
        });
      })
      .addCase(postMessage.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });
  },
});

export default chatSlice.reducer;
